import React, { useCallback, useEffect, useState } from 'react';

import AntdForm from 'antd/lib/form';

import { ReferenceDtoAutoriteFourriereDto } from 'lib_api/lib/api/gen';

import { EditableProfilFormContainer } from 'components/ProfilFormContainer';
import { useApi } from 'hooks/ApiStoreContext';
import { useMountedComponent } from 'hooks/useMountedComponent';
import { useUserStore } from 'hooks/UserStoreContext';
import { useHandleBackErrors } from 'hooks/utils/handleBackErrors';
import { useConfirmProfile } from 'hooks/utils/useConfirmProfil';
import {
  FicheProfilFourriere,
  FicheProfilFourriereBlocFourriere,
  SemaineHoraire,
} from 'types/FicheProfil/FicheProfilGardienFourriere';

import { generateFicheProfil, generateFourriereDTO } from '../utils';

import DatePickerDateDebutAgrement from './Inputs/DatePickerDateDebutAgrement';
import DatePickerDateFinAgrement from './Inputs/DatePickerDateFinAgrement';
import InputAdressComplement from './Inputs/InputAdressComplement';
import InputAgreementNumber from './Inputs/InputAgreementNumber';
import InputBossEmail from './Inputs/InputBossEmail';
import InputBossName from './Inputs/InputBossName';
import InputCity from './Inputs/InputCity';
import InputEmail from './Inputs/InputEmail';
import InputGPSLocalization from './Inputs/InputGPSLocalization';
import InputHoraire from './Inputs/InputHoraire';
import InputMobileNumber from './Inputs/InputMobileNumber';
import InputParkSize from './Inputs/InputParkSize';
import InputPhoneNumber from './Inputs/InputPhoneNumber';
import InputSocialReason from './Inputs/InputSocialReason';
import InputStreetName from './Inputs/InputStreetName';
import InputStreetNumber from './Inputs/InputStreetNumber';
import InputZIPCode from './Inputs/InputZIPCode';
import SelectAttachmentAuthority from './Inputs/SelectAttachmentAuthority';

const extractInitialValues = (
  values: FicheProfilFourriere,
): FicheProfilFourriereBlocFourriere => {
  return {
    socialReason: values.socialReason,
    agreementNumber: values.agreementNumber,
    agreementStartDate: values.agreementStartDate,
    agreementEndDate: values.agreementEndDate,
    attachmentAuthority: values.attachmentAuthority,
    parkSize: values.parkSize,
    streetNumber: values.streetNumber,
    streetName: values.streetName,
    adressComplement: values.adressComplement,
    zipCode: values.zipCode,
    city: values.city,
    phoneNumber: values.phoneNumber,
    mobileNumber: values.mobileNumber,
    email: values.email,
    gpsLocalization: values.gpsLocalization,
    bossName: values.bossName,
    bossEmail: values.bossEmail,
    horaires: values.horaires,
  };
};

interface FicheProfilProps {
  values: FicheProfilFourriere;
  refAutoriteFourriere: ReferenceDtoAutoriteFourriereDto;
  mustConfirmAccount: boolean | undefined;
  updateRequiredActions: () => void;
}

const FicheProfilFourriereForm: React.FC<FicheProfilProps> = ({
  values,
  refAutoriteFourriere,
  mustConfirmAccount,
  updateRequiredActions,
}: FicheProfilProps): React.ReactElement => {
  const user = useUserStore();
  const gardienFourriereControllerApi = useApi().GardienFourriereControllerApi;
  const behaviourOnError = useHandleBackErrors();
  const isMounted = useMountedComponent();
  const [data, setData] = useState(extractInitialValues(values));
  const [horaires, setHoraires] = useState<SemaineHoraire>(data.horaires);
  const [pForm] = AntdForm.useForm();
  const confirmProfile = useConfirmProfile(user.username);

  const generateRequestDto = useCallback(
    (valuesFourriere: FicheProfilFourriereBlocFourriere) => {
      return generateFourriereDTO({
        ...values,
        ...valuesFourriere,
        horaires,
      });
    },
    [horaires, values],
  );

  const submit = useCallback(
    (valuesFourriere: FicheProfilFourriereBlocFourriere) => {
      const newInfos = generateRequestDto(valuesFourriere);
      return gardienFourriereControllerApi
        .setFourriereInfoUsingPOST(
          user.idCorrelationFourriere ? user.idCorrelationFourriere : '',
          newInfos,
        )
        .then(async savedInfo => {
          if (!isMounted.current) {
            return;
          }
          const datas = generateFicheProfil(user, savedInfo);
          setData(datas);
          setHoraires(datas.horaires);

          // automatically confirm referentials edited by non admin users
          await confirmProfile({
            idFourriereToConfirm: savedInfo.id,
            mustConfirmAccount: mustConfirmAccount,
          });
          updateRequiredActions();
        })
        .catch((reason: Response) => {
          behaviourOnError(reason);
        });
    },
    [
      user,
      gardienFourriereControllerApi,
      behaviourOnError,
      setData,
      generateRequestDto,
      isMounted,
      confirmProfile,
      mustConfirmAccount,
      updateRequiredActions,
    ],
  );

  useEffect(() => {
    pForm.setFieldsValue(data);
  }, [pForm, data]);

  return (
    <EditableProfilFormContainer
      form={pForm}
      title="Ma fourrière"
      idForm="FourriereForm"
      initialValue={data}
      submit={submit}
    >
      {(data, isEdit) => (
        <>
          <InputSocialReason
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputAgreementNumber
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <DatePickerDateDebutAgrement
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <DatePickerDateFinAgrement
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <SelectAttachmentAuthority
            values={data}
            isEditable={isEdit}
            refAutoriteFourriere={refAutoriteFourriere}
            generateRequestDto={generateRequestDto}
          />
          <InputParkSize
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputStreetNumber
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputStreetName
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputAdressComplement
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputZIPCode
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputCity
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputPhoneNumber
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputMobileNumber
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputEmail
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputGPSLocalization
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputBossName
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputBossEmail
            values={data}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
          />
          <InputHoraire
            values={{ ...values, horaires: horaires }}
            isEditable={isEdit}
            generateRequestDto={generateRequestDto}
            setValues={setHoraires}
          />
        </>
      )}
    </EditableProfilFormContainer>
  );
};

export default FicheProfilFourriereForm;
